import React, { useState } from 'react';
import { MapPin, Plus, Navigation } from 'lucide-react';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { typography } from '../../config/typography';
import { spacing } from '../../config/spacing';
import { Card } from '../UI/card';
import { Button } from '../UI/button';
import { Input } from '../UI/input';
import { watchlistService } from '../../services/watchlistService';
import toast from 'react-hot-toast';

const cardTitleStyle = typography.textStyles?.cardTitle || {};
const labelStyle = typography.textStyles?.caption || { fontSize: '0.75rem', fontWeight: '500' };

function formatAddressLine(addr) {
  const street = addr.streetAddress ?? addr.street_address ?? '';
  const city = addr.city ?? '';
  const state = addr.stateProvince ?? addr.state_province ?? '';
  const postal = addr.postalCode ?? addr.postal_code ?? '';
  const country = addr.country ?? '';
  const cityStatePostal = [city, state, postal].filter(Boolean).join(', ');
  return { street, cityStatePostal, country };
}

export default function RoutesTabAddressCard({
  companyId,
  addresses = [],
  onAddressAdded,
  onOpenCreateRoute,
}) {
  const { t } = useLocale();
  const [formData, setFormData] = useState({
    streetAddress: '',
    city: '',
    stateProvince: '',
    postalCode: '',
    country: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const next = {};
    if (!formData.streetAddress?.trim()) next.streetAddress = t('fieldRequired');
    if (!formData.city?.trim()) next.city = t('fieldRequired');
    if (!formData.country?.trim()) next.country = t('fieldRequired');
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const userId = localStorage.getItem('userId') || JSON.parse(localStorage.getItem('user') || '{}').uid;
    if (!userId) {
      toast.error(t('loginRequired'));
      return;
    }
    setSubmitting(true);
    try {
      const result = await watchlistService.addUserAddress(userId, companyId, {
        ...formData,
        isPrimary: false,
      });
      if (result.success) {
        toast.success(t('addressAddedSuccess'));
        setFormData({ streetAddress: '', city: '', stateProvince: '', postalCode: '', country: '' });
        onAddressAdded?.();
      } else {
        toast.error(result.message || t('addressAddFailed'));
      }
    } catch (err) {
      toast.error(t('addressAddFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="p-6" style={{ borderColor: colors.border.light, borderRadius: spacing.radius?.lg ?? 12 }}>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Quick add form — 40% */}
        <div className="lg:w-[40%] flex-shrink-0">
          <h3
            className="font-semibold mb-4 flex items-center gap-2"
            style={{ color: colors.text.primary, ...cardTitleStyle }}
          >
            <Plus className="h-5 w-5" style={{ color: colors.primary[500] }} />
            {t('quickAddAddressTitle')}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block mb-1" style={{ color: colors.text.secondary, ...labelStyle }}>
                {t('streetAddressLabel')} *
              </label>
              <Input
                name="streetAddress"
                value={formData.streetAddress}
                onChange={handleChange}
                placeholder={t('streetAddressPlaceholder')}
                className="w-full"
                style={{ borderColor: errors.streetAddress ? colors.danger[500] : colors.border.light }}
              />
              {errors.streetAddress && (
                <p className="text-xs mt-0.5" style={{ color: colors.danger[500] }}>{errors.streetAddress}</p>
              )}
            </div>
            <div>
              <label className="block mb-1" style={{ color: colors.text.secondary, ...labelStyle }}>
                {t('cityLabel')} *
              </label>
              <Input
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={t('cityPlaceholder')}
                className="w-full"
                style={{ borderColor: errors.city ? colors.danger[500] : colors.border.light }}
              />
              {errors.city && (
                <p className="text-xs mt-0.5" style={{ color: colors.danger[500] }}>{errors.city}</p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block mb-1" style={{ color: colors.text.secondary, ...labelStyle }}>
                  {t('stateProvinceLabel')}
                </label>
                <Input
                  name="stateProvince"
                  value={formData.stateProvince}
                  onChange={handleChange}
                  placeholder={t('stateProvincePlaceholder')}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block mb-1" style={{ color: colors.text.secondary, ...labelStyle }}>
                  {t('postalCodeLabel')}
                </label>
                <Input
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder={t('postalCodePlaceholder')}
                  className="w-full"
                />
              </div>
            </div>
            <div>
              <label className="block mb-1" style={{ color: colors.text.secondary, ...labelStyle }}>
                {t('countryLabel')} *
              </label>
              <Input
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder={t('countryPlaceholder')}
                className="w-full"
                style={{ borderColor: errors.country ? colors.danger[500] : colors.border.light }}
              />
              {errors.country && (
                <p className="text-xs mt-0.5" style={{ color: colors.danger[500] }}>{errors.country}</p>
              )}
            </div>
            <Button type="submit" size="sm" disabled={submitting} className="w-full">
              <Plus className="h-4 w-4 mr-2" />
              {submitting ? t('adding') : t('addAddress')}
            </Button>
          </form>
        </div>

        {/* Right: Address list — 60% */}
        <div className="lg:flex-1 min-w-0 flex flex-col">
          <div className="flex items-center justify-between gap-4 mb-4">
            <h3
              className="font-semibold flex items-center gap-2 shrink-0"
              style={{ color: colors.text.primary, ...cardTitleStyle }}
            >
              <MapPin className="h-5 w-5" style={{ color: colors.primary[500] }} />
              {t('addressesTitle')} ({addresses.length})
            </h3>
            {onOpenCreateRoute && (
              <Button
                size="sm"
                onClick={onOpenCreateRoute}
                disabled={addresses.length < 2}
                variant="default"
                className="shrink-0"
              >
                <Navigation className="h-4 w-4 mr-2" />
                {t('addRoute')}
              </Button>
            )}
          </div>
          {addresses.length === 0 ? (
            <div
              className="rounded-xl border border-dashed py-10 px-4 text-center"
              style={{ borderColor: colors.border.light, backgroundColor: colors.background.secondary ?? colors.gray[50] }}
            >
              <MapPin className="h-10 w-10 mx-auto mb-2" style={{ color: colors.gray[400] }} />
              <p className="text-sm" style={{ color: colors.text.tertiary }}>{t('noAddressesAdded')}</p>
            </div>
          ) : (
            <ul className="space-y-3 overflow-auto max-h-[320px] pr-1">
              {addresses.map((addr) => {
                const { street, cityStatePostal, country } = formatAddressLine(addr);
                const isPrimary = addr.isPrimary === true || addr.is_primary === true;
                return (
                  <li
                    key={addr.addressId ?? addr.address_id}
                    className="rounded-xl border p-4 flex items-start gap-4 transition-colors hover:shadow-sm"
                    style={{
                      borderColor: colors.border.light,
                      backgroundColor: colors.background.card ?? colors.background.secondary ?? '#fff',
                      boxShadow: spacing.shadow?.sm,
                    }}
                  >
                    <div
                      className="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: colors.primary[100] }}
                    >
                      <MapPin className="h-5 w-5" style={{ color: colors.primary[600] }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      {isPrimary && (
                        <span
                          className="inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-2"
                          style={{ backgroundColor: colors.primary[100], color: colors.primary[700] }}
                        >
                          {t('primaryAddress')}
                        </span>
                      )}
                      {street && (
                        <p className="text-sm font-medium" style={{ color: colors.text.primary }}>
                          {street}
                        </p>
                      )}
                      {cityStatePostal && (
                        <p className="text-sm mt-0.5" style={{ color: colors.text.secondary }}>
                          {cityStatePostal}
                        </p>
                      )}
                      {country && (
                        <p className="text-sm mt-0.5" style={{ color: colors.text.tertiary }}>
                          {country}
                        </p>
                      )}
                      {!street && !cityStatePostal && !country && (
                        <p className="text-sm" style={{ color: colors.text.tertiary }}>{t('addressesTitle')}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
